import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useIMask } from 'react-imask';
import { Controller, FieldErrors, Control, FieldValues, Path } from 'react-hook-form';
import get from 'lodash/get';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';

import styles from './InputMask.module.scss';

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors;
  name: string;
  label: string;
  placeholder?: string;
  mask?: string;
  alwaysShowMask?: boolean;
  tabIndex?: number;
  endAdornment?: ReactNode;
  maskPlaceholder?: string;
}

const DIGIT_DEFINITIONS = { '9': /[0-9]/ };

interface IMaskedFieldProps {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  isError: boolean;
  errorText: string;
  placeholder?: string;
  mask: string;
  alwaysShowMask?: boolean;
  tabIndex?: number;
  endAdornment?: ReactNode;
  maskPlaceholder?: string;
}

const MaskedField = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  isError,
  errorText,
  placeholder,
  mask,
  alwaysShowMask,
  tabIndex,
  endAdornment,
  maskPlaceholder,
}: IMaskedFieldProps): ReactElement => {
  const [isFocused, setIsFocused] = useState(false);
  const { ref, setValue } = useIMask<HTMLInputElement>(
    {
      mask,
      definitions: DIGIT_DEFINITIONS,
      lazy: !alwaysShowMask,
      placeholderChar: maskPlaceholder,
      overwrite: true,
    },
    { defaultValue: value, onAccept: acceptedValue => onChange(acceptedValue) },
  );

  useEffect(() => {
    setValue(value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const maskPlaceholderText = mask.replace(/9/g, maskPlaceholder ?? '_');

  return (
    <FormControl variant="outlined" size="small" className={styles.inputContainer}>
      {label && (
        <InputLabel shrink error={isError}>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        inputRef={ref}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur();
        }}
        notched={!!label}
        label={label}
        type="tel"
        className="input"
        placeholder={placeholder ?? (isFocused ? maskPlaceholderText : undefined)}
        error={isError}
        endAdornment={endAdornment}
        inputProps={{ tabIndex, name }}
      />
      {isError && <FormHelperText error>{errorText.toString()}</FormHelperText>}
    </FormControl>
  );
};

const InputMaskComponent = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  errors,
  placeholder,
  mask = '999-999-9999',
  alwaysShowMask,
  tabIndex,
  endAdornment,
  maskPlaceholder,
}: IProps<TFieldValues>): ReactElement => {
  const errorText = get(errors, `${name}.message`, '');
  const isError = Boolean(errorText);

  return (
    <Controller
      name={name as Path<TFieldValues>}
      control={control}
      render={({ field: { value, onBlur, onChange } }) => (
        <MaskedField
          name={name}
          label={label}
          value={value as string}
          onChange={onChange}
          onBlur={onBlur}
          isError={isError}
          errorText={errorText.toString()}
          placeholder={placeholder}
          mask={mask}
          alwaysShowMask={alwaysShowMask}
          tabIndex={tabIndex}
          endAdornment={endAdornment}
          maskPlaceholder={maskPlaceholder}
        />
      )}
    />
  );
};

export default InputMaskComponent;
